'use client';

import { useState, useMemo } from 'react';
import { Search, Plus, Eye, Edit2, Factory, Package, Clock, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { PageHeader } from '../../../components/shared/PageHeader';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, Button } from '../../../components/shared/Modal';
import { boms, workstations } from '../data/mock-data';
import { bomStatusConfig, type BOM } from '../types';

// BOM 등록 모달
function BOMAddModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [items, setItems] = useState([{ itemName: '', qty: 1, uom: 'EA', rate: 0 }]);
  const [operations, setOperations] = useState([{ operationName: '', workstation: '', timeInMins: 60, hourRate: 0 }]);

  const addItem = () => setItems([...items, { itemName: '', qty: 1, uom: 'EA', rate: 0 }]);
  const removeItem = (index: number) => { if (items.length > 1) setItems(items.filter((_, i) => i !== index)); };

  const addOperation = () => setOperations([...operations, { operationName: '', workstation: '', timeInMins: 60, hourRate: 0 }]);
  const removeOperation = (index: number) => { if (operations.length > 1) setOperations(operations.filter((_, i) => i !== index)); };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="BOM 등록"
      description="새로운 제품 구성표(Bill of Materials)를 등록합니다."
      size="xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button onClick={onClose}>등록</Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <FormField label="BOM 코드" required>
            <FormInput placeholder="자동 생성" disabled />
          </FormField>
          <FormField label="BOM 이름" required>
            <FormInput placeholder="BOM 이름을 입력하세요" />
          </FormField>
          <FormField label="품목 코드" required>
            <FormSelect>
              <option value="">품목을 선택하세요</option>
              <option value="ITM-001">ITM-001 - 택배 박스 (소)</option>
              <option value="ITM-002">ITM-002 - 택배 박스 (중)</option>
              <option value="ITM-003">ITM-003 - 택배 박스 (대)</option>
            </FormSelect>
          </FormField>
          <FormField label="기본 BOM">
            <div className="flex items-center gap-2 h-10">
              <input type="checkbox" id="isDefault" className="rounded border-border" />
              <label htmlFor="isDefault" className="text-sm">기본 BOM으로 설정</label>
            </div>
          </FormField>
        </div>

        {/* 원자재 목록 */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium flex items-center gap-2"><Package className="w-4 h-4" />원자재</h3>
            <Button variant="outline" size="sm" onClick={addItem}>
              <Plus className="w-4 h-4 mr-1" />원자재 추가
            </Button>
          </div>
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">원자재명</th>
                  <th className="text-right p-3 font-medium w-20">수량</th>
                  <th className="text-center p-3 font-medium w-20">단위</th>
                  <th className="text-right p-3 font-medium w-28">단가</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((_, index) => (
                  <tr key={index} className="border-t border-border">
                    <td className="p-2"><FormInput placeholder="원자재명" /></td>
                    <td className="p-2"><FormInput type="number" min={1} defaultValue={1} className="text-right" /></td>
                    <td className="p-2">
                      <FormSelect>
                        <option value="EA">EA</option>
                        <option value="M">M</option>
                        <option value="KG">KG</option>
                        <option value="L">L</option>
                      </FormSelect>
                    </td>
                    <td className="p-2"><FormInput type="number" min={0} defaultValue={0} className="text-right" /></td>
                    <td className="p-2">
                      <button onClick={() => removeItem(index)} className="p-1 rounded hover:bg-accent" disabled={items.length === 1}>
                        <Trash2 className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 공정 목록 */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium flex items-center gap-2"><Clock className="w-4 h-4" />공정</h3>
            <Button variant="outline" size="sm" onClick={addOperation}>
              <Plus className="w-4 h-4 mr-1" />공정 추가
            </Button>
          </div>
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">공정명</th>
                  <th className="text-left p-3 font-medium w-36">작업장</th>
                  <th className="text-right p-3 font-medium w-24">시간(분)</th>
                  <th className="text-right p-3 font-medium w-28">시간당 단가</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {operations.map((_, index) => (
                  <tr key={index} className="border-t border-border">
                    <td className="p-2"><FormInput placeholder="공정명" /></td>
                    <td className="p-2">
                      <FormSelect>
                        <option value="">선택</option>
                        {workstations.filter(ws => ws.isActive).map((ws) => (
                          <option key={ws.id} value={ws.id}>{ws.name}</option>
                        ))}
                      </FormSelect>
                    </td>
                    <td className="p-2"><FormInput type="number" min={1} defaultValue={60} className="text-right" /></td>
                    <td className="p-2"><FormInput type="number" min={0} defaultValue={0} className="text-right" /></td>
                    <td className="p-2">
                      <button onClick={() => removeOperation(index)} className="p-1 rounded hover:bg-accent" disabled={operations.length === 1}>
                        <Trash2 className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export function BOMListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [expandedBOM, setExpandedBOM] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    return boms.filter((bom) => {
      const matchesSearch =
        bom.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        bom.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        bom.itemCode.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = selectedStatus === 'all' || bom.status === selectedStatus;

      return matchesSearch && matchesStatus;
    });
  }, [searchQuery, selectedStatus]);

  const toggleExpand = (id: string) => {
    setExpandedBOM(expandedBOM === id ? null : id);
  };

  // 상태별 통계
  const statusStats = useMemo(() => {
    const stats: Record<string, number> = {};
    boms.forEach((bom) => {
      stats[bom.status] = (stats[bom.status] || 0) + 1;
    });
    return stats;
  }, []);

  const getStatusColor = (color: string) => {
    const colors: Record<string, string> = {
      green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      gray: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
      red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    };
    return colors[color] || colors.gray;
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="BOM 관리"
        description="제품 구성표(Bill of Materials)를 조회하고 관리합니다."
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            BOM 등록
          </button>
        }
      />

      {/* 상태별 요약 */}
      <div className="grid grid-cols-3 gap-3">
        {Object.entries(bomStatusConfig).map(([status, config]) => (
          <button
            key={status}
            onClick={() => setSelectedStatus(selectedStatus === status ? 'all' : status)}
            className={`p-3 rounded-lg border text-left transition-colors ${
              selectedStatus === status
                ? 'border-primary bg-primary/5'
                : 'border-border bg-card hover:bg-accent/50'
            }`}
          >
            <div className="text-xs text-muted-foreground">{config.label}</div>
            <div className="text-xl font-bold mt-1">{statusStats[status] || 0}개</div>
          </button>
        ))}
      </div>

      {/* 필터 */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="BOM 코드, 이름, 품목코드 검색..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 상태</option>
          {Object.entries(bomStatusConfig).map(([status, config]) => (
            <option key={status} value={status}>
              {config.label}
            </option>
          ))}
        </select>
      </div>

      {/* BOM 목록 */}
      <div className="space-y-4">
        {filteredData.map((bom) => {
          const isExpanded = expandedBOM === bom.id;
          const statusConfig = bomStatusConfig[bom.status];

          return (
            <div
              key={bom.id}
              className="rounded-lg border border-border bg-card overflow-hidden"
            >
              {/* BOM 헤더 */}
              <div
                className="p-4 cursor-pointer hover:bg-accent/50 transition-colors"
                onClick={() => toggleExpand(bom.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                      <Factory className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-primary">{bom.code}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(statusConfig.color)}`}>
                          {statusConfig.label}
                        </span>
                        {bom.isDefault && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                            기본
                          </span>
                        )}
                      </div>
                      <div className="font-medium mt-1">{bom.name}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <div className="text-sm text-muted-foreground">원가</div>
                      <div className="font-bold">₩{bom.totalCost.toLocaleString()}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        className="p-1.5 rounded hover:bg-accent"
                        title="보기"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Eye className="w-4 h-4 text-muted-foreground" />
                      </button>
                      <button
                        className="p-1.5 rounded hover:bg-accent"
                        title="수정"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Edit2 className="w-4 h-4 text-muted-foreground" />
                      </button>
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* BOM 상세 */}
              {isExpanded && (
                <div className="border-t border-border p-4 bg-accent/30">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* 원자재 목록 */}
                    <div>
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        원자재 ({bom.items.length}개)
                      </h4>
                      <div className="space-y-2">
                        {bom.items.map((item) => (
                          <div
                            key={item.id}
                            className="p-2 rounded bg-background border border-border text-sm"
                          >
                            <div className="flex justify-between">
                              <span className="font-medium">{item.itemName}</span>
                              <span>₩{item.amount.toLocaleString()}</span>
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                              {item.itemCode} | {item.qty} {item.uom} × ₩{item.rate.toLocaleString()}
                            </div>
                          </div>
                        ))}
                        <div className="p-2 rounded bg-primary/10 text-sm font-medium flex justify-between">
                          <span>재료비 합계</span>
                          <span>₩{bom.materialCost.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>

                    {/* 공정 목록 */}
                    <div>
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        공정 ({bom.operations.length}개)
                      </h4>
                      <div className="space-y-2">
                        {bom.operations.map((op, index) => (
                          <div
                            key={op.id}
                            className="p-2 rounded bg-background border border-border text-sm"
                          >
                            <div className="flex justify-between">
                              <span className="font-medium">
                                {index + 1}. {op.operationName}
                              </span>
                              <span>₩{op.operationCost.toLocaleString()}</span>
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                              {op.workstation} | {op.timeInMins}분 × ₩{op.hourRate.toLocaleString()}/시간
                            </div>
                          </div>
                        ))}
                        <div className="p-2 rounded bg-primary/10 text-sm font-medium flex justify-between">
                          <span>가공비 합계</span>
                          <span>₩{bom.operationCost.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 원가 요약 */}
                  <div className="mt-4 p-3 rounded-lg bg-primary/10 border border-primary/20">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-sm text-muted-foreground">재료비</div>
                        <div className="font-bold">₩{bom.materialCost.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">가공비</div>
                        <div className="font-bold">₩{bom.operationCost.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">총 원가</div>
                        <div className="font-bold text-primary">₩{bom.totalCost.toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredData.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          검색 결과가 없습니다.
        </div>
      )}

      {/* BOM 등록 모달 */}
      <BOMAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
